import React, { useState, useEffect, useMemo } from 'react';
import NavigationBar from '../NavigationBar';
import axios from '../axios';
import { Container, Row, Col, Table, Button, Form, Badge, Alert } from 'react-bootstrap';
import { formatNumberWithCommas } from '../utilities';
import { notify, errorMessage } from '../notify';

// Paper balance for one day: opening + received - used = closing, per paper and roll size.
// A closed day shows the snapshot saved at day close; an open day shows the live stock and what came in.

const bdToday = () => new Date(Date.now() + 6 * 3600 * 1000).toISOString().slice(0, 10);
const addDays = (d, n) => {
    const t = new Date(`${d}T00:00:00Z`);
    t.setUTCDate(t.getUTCDate() + n);
    return t.toISOString().slice(0, 10);
};
const kg = (n) => (n == null ? '-' : formatNumberWithCommas(Math.round(Number(n) || 0)));
const cell = { padding: '3px 6px', verticalAlign: 'middle' };
const head = { ...cell, backgroundColor: '#d1e7dd', whiteSpace: 'nowrap' };
const sumRow = { ...cell, backgroundColor: '#fff3cd', fontWeight: 'bold' };

function PaperBalance() {
    const [date, setDate] = useState(bdToday());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [bySize, setBySize] = useState(false);

    useEffect(() => {
        setLoading(true);
        axios.get(`${window.backendUrl}/paper_balance`, { params: { date } })
            .then((res) => setData(res.data))
            .catch((err) => notify.error(errorMessage(err, 'Could not load the balance')))
            .finally(() => setLoading(false));
    }, [date]);

    const cols = ['OpeningKg', 'ReceivedKg', 'BoardKg', 'OtherKg', 'CoreKg', 'ClosingKg'];
    const groups = useMemo(() => {
        const out = {};
        (data?.lines || []).forEach((l) => {
            const name = `${l.Name || 'Unknown'} ${l.GSMValue || ''}`.trim();
            const g = out[name] || (out[name] = { name, lines: [], tot: { ClosingRolls: 0, ClosingValue: 0 } });
            g.lines.push(l);
            cols.forEach((c) => { if (l[c] != null) g.tot[c] = (g.tot[c] || 0) + Number(l[c]); });
            g.tot.ClosingRolls += Number(l.ClosingRolls) || 0;
            g.tot.ClosingValue += Number(l.ClosingValue) || 0;
        });
        return Object.values(out);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);
    const grand = useMemo(() => {
        const t = { ClosingRolls: 0, ClosingValue: 0 };
        groups.forEach((g) => {
            cols.forEach((c) => { if (g.tot[c] != null) t[c] = (t[c] || 0) + g.tot[c]; });
            t.ClosingRolls += g.tot.ClosingRolls;
            t.ClosingValue += g.tot.ClosingValue;
        });
        return t;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groups]);

    const closed = !!data?.closed;
    // every roll weight includes its core, so "paper" = weight - core kg x rolls
    const coreKgEach = Number(data?.coreWeightKg) || 8;
    const valueCells = (t) => (
        <>
            <td style={cell}>{closed ? kg(t.OpeningKg) : '-'}</td>
            <td style={cell}>{kg(t.ReceivedKg)}</td>
            <td style={cell}>{closed ? kg(t.BoardKg) : '-'}</td>
            <td style={cell}>{closed ? kg(t.OtherKg) : '-'}</td>
            <td style={cell}>{closed ? kg(t.CoreKg) : '-'}</td>
            <td style={cell}>{kg(t.ClosingKg)}</td>
            <td style={cell}><strong>{kg(Math.max(0, (Number(t.ClosingKg) || 0) - coreKgEach * (Number(t.ClosingRolls) || 0)))}</strong></td>
            <td style={cell}>{formatNumberWithCommas(t.ClosingRolls || 0)}</td>
            <td style={cell}>{kg(t.ClosingValue)}</td>
        </>
    );

    return (
        <>
            <NavigationBar />
            <Container fluid>
                <Row className="justify-content-center mt-3">
                    <Col md={11} lg={10}>
                        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                            <h4 className="mb-0 me-3">Paper Balance</h4>
                            <Button size="sm" variant="outline-secondary" onClick={() => setDate(addDays(date, -1))}>‹</Button>
                            <Form.Control type="date" size="sm" style={{ width: 160 }} value={date} onChange={(e) => e.target.value && setDate(e.target.value)} />
                            <Button size="sm" variant="outline-secondary" onClick={() => setDate(addDays(date, 1))}>›</Button>
                            <Button size="sm" variant="outline-secondary" onClick={() => setDate(bdToday())}>Today</Button>
                            {data && (closed ? <Badge bg="success">Day closed</Badge> : <Badge bg="warning" text="dark">Not closed — live stock</Badge>)}
                            <Form.Check type="switch" className="ms-3" label="Show each roll size" checked={bySize} onChange={(e) => setBySize(e.target.checked)} />
                            {loading && <span className="text-muted small">Loading…</span>}
                        </div>
                        {data && !closed && (
                            <Alert variant="light" className="py-2 small">
                                This day is not closed yet, so "used" is not known. Closing shows the stock now plus what was received on {date}. Close the day in Board Making Plan to see the full balance.
                            </Alert>
                        )}
                        {data && (
                            <div className="mb-2">
                                <Badge bg="light" text="dark" style={{ fontSize: 13, fontWeight: 'normal' }}>
                                    Paper left on cores (all closed days up to {date}, not counting the cores): <strong>{kg(data.coreLeftoverKg)} kg</strong>
                                </Badge>
                            </div>
                        )}
                        <Table bordered size="sm" style={{ fontSize: 13 }}>
                            <thead><tr>
                                <th style={head}>Paper</th><th style={head}>Roll"</th><th style={head}>Opening kg</th><th style={head}>Received kg</th>
                                <th style={head}>Used on board kg</th><th style={head}>Other use kg</th><th style={head}>Cores + paper left on cores kg</th>
                                <th style={head}>Closing kg (with cores)</th><th style={head}>Closing paper kg</th><th style={head}>Rolls</th><th style={head}>Value Tk</th>
                            </tr></thead>
                            <tbody>
                                {groups.map((g) => (
                                    <React.Fragment key={g.name}>
                                        {bySize && g.lines.map((l) => (
                                            <tr key={`${g.name}|${l.RollSize}`}>
                                                <td style={cell}>{g.name}</td>
                                                <td style={cell}>{Number(l.RollSize)}</td>
                                                {valueCells(l)}
                                            </tr>
                                        ))}
                                        <tr>
                                            <td style={bySize ? sumRow : cell}><strong>{g.name}</strong></td>
                                            <td style={bySize ? sumRow : cell}>{bySize ? 'all' : g.lines.length > 1 ? `${g.lines.length} sizes` : Number(g.lines[0].RollSize)}</td>
                                            {valueCells(g.tot)}
                                        </tr>
                                    </React.Fragment>
                                ))}
                                {groups.length > 0 && (
                                    <tr style={{ fontWeight: 'bold', backgroundColor: '#b7dfc4' }}>
                                        <td style={cell} colSpan={2}>Total</td>
                                        {valueCells(grand)}
                                    </tr>
                                )}
                                {data && !groups.length && <tr><td colSpan={11} className="text-center text-muted" style={cell}>No paper in stock</td></tr>}
                            </tbody>
                        </Table>
                    </Col>
                </Row>
            </Container>
        </>
    );
}

export default PaperBalance;
